import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Calendar, 
  User, 
  Loader2, 
  FileText, 
  Flame, 
  ArrowRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import axios from 'axios';
import RoadTo53 from '../components/custom/RoadTo53';
import { TeamSnapshot } from '../components/custom/TeamSnapshot';
import { FanPoll, Storylines } from '../components/custom/TeamHubSections';
import { useSeo } from '../components/custom/Seo';
import { logError } from '../lib/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CommandersPage = () => {
  const [featuredModule, setFeaturedModule] = useState('roadto53');

  useEffect(() => {
    axios.get(`${API}/team-hub/settings`)
      .then((r) => setFeaturedModule(r.data.settings?.commanders || 'roadto53'))
      .catch(() => {});
  }, []);

  useSeo({
    title: 'Washington Commanders News | DC Sports Report',
    description:
      'The latest Washington Commanders news, analysis, Road to 53 roster tracking and coverage from DC Sports Report, an independent Washington, D.C. sports publication.',
    path: '/commanders',
  });
  const [articles, setArticles] = useState([]);
  const [loadingArticles, setLoadingArticles] = useState(true);

  const fetchArticles = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/articles/team/commanders`);
      setArticles(response.data.articles || []);
    } catch (error) {
      logError('Failed to load articles:', error);
    } finally {
      setLoadingArticles(false);
    }
  }, []); // Empty deps - API is constant

  useEffect(() => {
    fetchArticles();
  }, [fetchArticles]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const isNew = (dateString) => {
    const date = new Date(dateString);
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    return date >= weekAgo;
  };

  const team = {
    name: 'Commanders',
    fullName: 'Washington Commanders',
    sport: 'NFL Football',
    primaryColor: '#5A1414',
    secondaryColor: '#FFB612',
  };

  const featuredArticle = articles[0];
  const otherArticles = articles.slice(1);

  return (
    <div data-testid="commanders-page" className="min-h-screen bg-slate-100">
      {/* Hero Header */}
      <div className="py-12 sm:py-16" style={{ backgroundColor: team.primaryColor }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/">
            <Button
              variant="outline"
              data-testid="back-to-home-button"
              className="mb-6 bg-black/20 hover:bg-black/40 text-white border-white/30 hover:border-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </Link>
          
          <div 
            className="inline-block px-4 py-1 text-xs font-bold uppercase tracking-widest mb-4 border-2"
            style={{ backgroundColor: team.secondaryColor, color: team.primaryColor, borderColor: 'white' }}
          >
            {team.sport}
          </div>
          
          <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl text-white uppercase tracking-tight leading-none">
            {team.fullName}
          </h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Featured Seasonal Tracker: Road to 53 */}
        {featuredModule !== 'season' && (
          <div className="mb-12">
            <RoadTo53 />
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content - Articles */}
          <div className="lg:col-span-2 space-y-8">
            {/* Featured Article */}
            {featuredArticle && (
              <Link to={`/article/${featuredArticle.id}`}>
                <Card 
                  className="border-4 border-black shadow-brutal-lg hover:-translate-y-2 transition-all cursor-pointer overflow-hidden"
                  style={{ borderTopColor: team.primaryColor, borderTopWidth: '8px' }}
                >
                  <CardContent className="p-0">
                    {featuredArticle.has_image && (
                      <div className="w-full max-h-72 overflow-hidden bg-slate-200">
                        <img
                          src={`${API}/articles/${featuredArticle.id}/image`}
                          alt={featuredArticle.title}
                          className="w-full h-72 object-cover"
                        />
                      </div>
                    )}
                    <div className="grid md:grid-cols-2">
                      <div className="p-6 flex flex-col justify-center" style={{ backgroundColor: team.primaryColor }}>
                        <div className="flex items-center gap-2 mb-4">
                          {isNew(featuredArticle.created_at) && (
                            <span className="bg-[#FFB612] text-[#5A1414] px-3 py-1 text-xs font-bold uppercase tracking-wide animate-pulse flex items-center gap-1">
                              <Flame className="w-3 h-3" />
                              New
                            </span>
                          )}
                          <span className="text-white/70 text-sm font-bold uppercase">Featured</span>
                        </div>
                        <h2 className="font-heading text-xl sm:text-2xl text-white uppercase tracking-tight leading-tight">
                          {featuredArticle.title}
                        </h2>
                        <div className="mt-4 flex items-center gap-4 text-white/70 text-sm">
                          <span className="flex items-center gap-1">
                            <User className="w-4 h-4" />
                            {featuredArticle.author}
                          </span>
                        </div>
                      </div>
                      <div className="p-6 bg-white flex flex-col justify-center">
                        <p className="text-slate-600 leading-relaxed line-clamp-3 mb-4">
                          {featuredArticle.summary || featuredArticle.content.substring(0, 200) + '...'}
                        </p>
                        <div className="flex items-center gap-2 text-[#5A1414] font-bold uppercase tracking-wide group">
                          Read More <ArrowRight className="w-4 h-4 group-hover:translate-x-2 transition-transform" />
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            )}

            {/* Other Articles */}
            {otherArticles.length > 0 && (
              <div>
                <h2 className="font-heading text-2xl text-slate-900 uppercase tracking-tight mb-6 flex items-center gap-2">
                  <FileText className="w-6 h-6" style={{ color: team.primaryColor }} />
                  More Articles
                </h2>
                <div className="space-y-4">
                  {otherArticles.map((article) => (
                    <Link key={article.id} to={`/article/${article.id}`}>
                      <Card className="border-2 border-slate-200 hover:border-black transition-all hover:-translate-y-1 shadow-sm hover:shadow-brutal cursor-pointer group overflow-hidden">
                        <CardContent className="p-0">
                          <div className="flex gap-4">
                            {article.has_image && (
                              <div className="w-32 sm:w-44 h-28 flex-shrink-0 overflow-hidden bg-slate-200">
                                <img
                                  src={`${API}/articles/${article.id}/image`}
                                  alt={article.title}
                                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                />
                              </div>
                            )}
                            <div className="flex-1 p-4">
                              <div className="flex items-center gap-2 mb-2">
                                {isNew(article.created_at) && (
                                  <span className="bg-[#FFB612] text-[#5A1414] px-2 py-1 text-xs font-bold uppercase animate-pulse">New</span>
                                )}
                              </div>
                              <h3 className="font-heading text-lg text-slate-900 uppercase tracking-tight group-hover:text-[#5A1414]">
                                {article.title}
                              </h3>
                              <div className="mt-2 flex items-center gap-4 text-sm text-slate-500">
                                <span className="flex items-center gap-1"><User className="w-3 h-3" />{article.author}</span>
                                <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{formatDate(article.created_at)}</span>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {loadingArticles && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin" style={{ color: team.primaryColor }} />
              </div>
            )}

            {!loadingArticles && articles.length === 0 && (
              <Card className="border-2 border-dashed border-slate-300">
                <CardContent className="p-8 text-center">
                  <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">Articles coming soon!</p>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar - Season Resources (archived seasonal trackers) */}
          <div className="lg:col-span-1 space-y-6">
            <h2 className="font-heading text-lg text-slate-900 uppercase tracking-wide flex items-center gap-2 border-b border-slate-200 pb-2">
              <span className="inline-block w-1 h-5 bg-[#5A1414]" />
              Season Resources
            </h2>
            {/* Current Season Snapshot */}
            <TeamSnapshot teamKey="commanders" />
            <Storylines teamKey="commanders" />
            <FanPoll teamKey="commanders" />
          </div>
        </div>

        {/* Road to 53 as archived resource when regular season is featured */}
        {featuredModule === 'season' && (
          <div className="mt-12">
            <h2 className="font-heading text-lg text-slate-900 uppercase tracking-wide flex items-center gap-2 border-b border-slate-200 pb-2 mb-6">
              <span className="inline-block w-1 h-5 bg-[#5A1414]" />
              Preseason Archive
            </h2>
            <RoadTo53 />
          </div>
        )}
      </div>

      {/* Bottom Accent */}
      <div className="h-4" style={{ backgroundColor: team.secondaryColor }} />
    </div>
  );
};

export default CommandersPage;
